import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaIcon } from '../FaIcon';
import { PortalReportsApi } from '../../api/portalReports';
import { DuplicateViewModal } from '../client/DuplicateViewModal';

// Botón "Compartir ▾" del Explorador V2.
// Unifica las acciones de share en un solo menú:
//   1. Asignar / Desasignar al portal del cliente (toggle directo via API)
//   2. Duplicar a otro cliente (reusa DuplicateViewModal)
//   3. Copiar enlace con filtros (callback handleShareUrl del Page)
//   4. Enviar por email (disabled, placeholder)
//
// Reemplaza al botón link "Compartir enlace" del Toolbar V1 y al switch
// "Asignar al portal" del SavedViewBanner V1. Visible solo cuando flag V2 activo.
class ShareMenuComponent extends Component {
    constructor(props) {
        super(props);
        this.state = { isMenuOpen: false, showDuplicate: false, toggling: false };
        this.menuRef = React.createRef();
        this.handleClickOutside = this.handleClickOutside.bind(this);
        // Inline styles para los items deshabilitados — inline para que el componente
        // sea autosuficiente sin tocar `_chart-controls.scss` ni agregar clases nuevas.
        this._disabledItemStyle = { opacity: 0.45, cursor: 'not-allowed' };
        // Override del min-width del dropdown (default 180px). 240px deja a
        // "Asignar al portal del cliente" en una sola línea — el item más largo del menú.
        this._menuStyle = { minWidth: 240 };
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside(event) {
        if (this.menuRef.current && !this.menuRef.current.contains(event.target)) {
            this.setState({ isMenuOpen: false });
        }
    }

    toggleMenu = () => {
        this.setState((s) => ({ isMenuOpen: !s.isMenuOpen }));
    };

    closeMenu = () => this.setState({ isMenuOpen: false });

    handleCopyLink = () => {
        this.closeMenu();
        if (this.props.onShareUrl) this.props.onShareUrl();
    };

    handleOpenDuplicate = () => {
        this.closeMenu();
        this.setState({ showDuplicate: true });
    };

    handleToggleAssign = () => {
        const { t, view, onViewUpdated } = this.props;
        if (!view || this.state.toggling) return;

        const currentlyAssigned = !!view.assigned_to_portal;
        const assignmentId = view.assignment_id || (view.assignment && view.assignment.id) || null;
        if (currentlyAssigned && !assignmentId) {
            toast.error(t('saved_views.toggle_error'));
            return;
        }

        this.setState({ toggling: true });
        const action = currentlyAssigned
            ? PortalReportsApi.unassignFromPortal(assignmentId)
            : PortalReportsApi.assignToPortal(view.id);

        action.then((response) => {
            this.setState({ toggling: false });
            if (!response || response.status === 'ERROR') {
                toast.error((response && response.error_message) || t('saved_views.toggle_error'));
                return;
            }
            toast.success(currentlyAssigned ? t('saved_views.unassigned') : t('saved_views.assigned'));
            this.closeMenu();
            if (onViewUpdated) {
                onViewUpdated({
                    ...view,
                    assigned_to_portal: !currentlyAssigned,
                    portal_order: !currentlyAssigned ? (response.order ?? null) : null,
                    assignment_id: !currentlyAssigned ? (response.id ?? null) : null,
                });
            }
        }).catch(() => {
            this.setState({ toggling: false });
            toast.error(t('saved_views.toggle_error'));
        });
    };

    render() {
        const { t, view, onShareUrl, agencyClientId } = this.props;
        const { isMenuOpen, showDuplicate, toggling } = this.state;
        const hasView = !!view;
        const isAssigned = hasView && !!view.assigned_to_portal;
        const canCopyLink = !!onShareUrl;

        return (
            <div
                className="data-explorer-share-menu-wrap"
                ref={this.menuRef}
                style={{ position: 'relative', display: 'inline-block' }}
            >
                <button
                    type="button"
                    className="btn chart-controls__action-button no-margin-bottom"
                    title={t('share_menu.button_title', 'Compartir')}
                    aria-label={t('share_menu.button_title', 'Compartir')}
                    aria-expanded={isMenuOpen}
                    onClick={this.toggleMenu}
                >
                    <FaIcon iconName="share-nodes" />
                </button>

                {isMenuOpen && (
                    <div className="chart-controls__adjust-menu" style={this._menuStyle}>
                        <div className="chart-controls__adjust-menu-title">
                            {t('share_menu.title', 'Compartir')}
                        </div>

                        {/* 1. Toggle Asignar / Desasignar al portal */}
                        <div
                            className="chart-controls__adjust-menu-option"
                            onClick={hasView && !toggling ? this.handleToggleAssign : undefined}
                            style={(!hasView || toggling) ? this._disabledItemStyle : undefined}
                            title={!hasView ? t('share_menu.assign_requires_view', 'Guardá la vista primero para asignarla al portal') : undefined}
                        >
                            <FaIcon iconName="users" style={{ marginRight: '0.5em' }} />
                            {isAssigned
                                ? t('share_menu.unassign_action', 'Desasignar del portal del cliente')
                                : t('share_menu.assign_action', 'Asignar al portal del cliente')}
                        </div>

                        {/* 2. Duplicar a otro cliente */}
                        <div
                            className="chart-controls__adjust-menu-option"
                            onClick={hasView ? this.handleOpenDuplicate : undefined}
                            style={!hasView ? this._disabledItemStyle : undefined}
                            title={!hasView ? t('share_menu.duplicate_requires_view', 'Guardá la vista primero para duplicarla') : undefined}
                        >
                            <FaIcon iconName="copy" style={{ marginRight: '0.5em' }} />
                            {t('share_menu.duplicate_to_others', 'Duplicar a otro cliente…')}
                        </div>

                        {/* 3. Copiar enlace con filtros */}
                        <div
                            className="chart-controls__adjust-menu-option"
                            onClick={canCopyLink ? this.handleCopyLink : undefined}
                            style={!canCopyLink ? this._disabledItemStyle : undefined}
                        >
                            <FaIcon iconName="link" style={{ marginRight: '0.5em' }} />
                            {t('share_menu.copy_link', 'Copiar enlace con filtros')}
                        </div>
                    </div>
                )}

                <DuplicateViewModal
                    isOpen={showDuplicate}
                    toggle={() => this.setState({ showDuplicate: false })}
                    sourceView={view}
                    currentClientId={view ? view.agency_client_id : agencyClientId}
                    onDuplicated={() => this.setState({ showDuplicate: false })}
                />
            </div>
        );
    }
}

export const ShareMenu = withTranslation()(ShareMenuComponent);
export default ShareMenu;
